import React, { useEffect, useRef, memo, ReactElement } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { WithLoadingSpinner } from "@components/common";
import { ChartWrapper } from "../common";
import { useChartOptions } from "./useChartOptions";

export interface DonutChartDataItem {
  id: string;
  name: string;
  value: number;
  y?: number;
  color?: string;
  textColor?: string;
  legendColor?: string;
  icon?: ReactElement;
  children?: DonutChartDataItem[];
  parentId?: string | null;
  hiddenInChart?: boolean;
  tooltipData?: {
    amount: number;
    name: string;
    id: number;
  }[];
}

type Props = {
  chartData?: DonutChartDataItem[];
  selectedId: string | null;
  height?: number;
  width?: number;
  isFetching?: boolean;
  onSectionClick?: (id: string) => void;
  showLabels?: boolean;
  disabled?: boolean;
  centerDetails?: {
    title?: string;
    value?: number;
    innerSize?: string;
    titleSize?: string;
    valueSize?: string;
  };
  tooltipType?: "table" | "percentage";
  hoveredId?: string;
  setHoveredId?: (id?: string) => void;
};

export const DonutChartWithTotal = memo(
  ({
    chartData,
    selectedId,
    height = 435,
    width = 540,
    onSectionClick,
    isFetching,
    showLabels = true,
    disabled = false,
    tooltipType = "table",
    centerDetails,
    hoveredId,
    setHoveredId,
  }: Props) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const finishedAnimating =
      chartComponentRef?.current?.chart.series[0]?.finishedAnimating;
    const chartOptions = useChartOptions({
      chartData,
      selectedId,
      height,
      width,
      isFetching: isFetching ?? false,
      onSectionClick,
      showLabels,
      disabled,
      centerDetails,
      tooltipType,
      setHoveredId,
    });

    useEffect(() => {
      if (!finishedAnimating) return;
      chartComponentRef.current?.chart?.reflow();
    }, [chartOptions, finishedAnimating]);

    useEffect(() => {
      const chart = chartComponentRef.current?.chart;
      // @ts-ignore
      if (!chart || !chart.series?.[0] || !chart.series[0].finishedAnimating)
        return;

      const series = chart.series[0];
      const hovered = series.data.find(
        (p: any) => p.id?.toString() === hoveredId
      );

      requestAnimationFrame(() => {
        series.points.forEach((pt: any) => {
          pt.setState("");
          pt.update(
            { opacity: hovered ? (pt === hovered ? 1 : 0.5) : 1 },
            false
          );
        });

        if (hovered) hovered.setState("hover");

        chart.redraw();

        if (hovered) {
          chart.tooltip.refresh(hovered);
        } else {
          chart.tooltip.hide();
        }
      });
    }, [hoveredId]);

    return (
      <WithLoadingSpinner
        isLoading={Boolean(isFetching)}
        style={{ height: height, display: "flex", justifyContent: "center" }}
      >
        <ChartWrapper>
          <HighchartsReact
            highcharts={Highcharts}
            ref={chartComponentRef}
            options={chartOptions}
            isFetching={Boolean(isFetching)}
          />
        </ChartWrapper>
      </WithLoadingSpinner>
    );
  }
);
